import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { Save, Loader2, CheckCircle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Field, ErrorState } from '../../components/ui';

// Reusable form-fill component: a client's Forms tab, and carers filling a form on a shift.
// Props: formId (required), shiftId, clientId, draft, title, onSubmitted, onCancel
//   draft  the client's saved draft of this form, if any: its answers are loaded to carry on with
//          (the server keeps one draft per client per form and updates it in place)
//   title  stored on the submission, as the Forms page does
// "Save draft" keeps the answers as they are; "Complete and submit" first needs every required
// question answered, the same rule as the Forms page, and stores the form as completed (only a
// completed form counts in the client's completion summary and review dates).

// Answers are keyed by question id, or by label for questions saved without one.
const keyOf = (q) => q.id || q.label;

const questionsOf = (form) => {
  const q = form && form.questions;
  if (Array.isArray(q)) return q;
  if (typeof q === 'string') {
    try { const v = JSON.parse(q); return Array.isArray(v) ? v : []; } catch { return []; }
  }
  return [];
};

// An answer counts as given unless it is empty: a "No" (false) is an answer, an untouched question is not.
const isBlank = (v) => v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length);

// The required questions still unanswered. Shared with the Forms page, so completing a form
// checks the same thing wherever it is filled in.
export function missingRequired(questions, answers) {
  return (questions || []).filter((q) => q && q.required && q.type !== 'heading' && isBlank((answers || {})[keyOf(q)]));
}

export function missingMessage(missing) {
  const names = missing.slice(0, 3).map((m) => m.label || 'Untitled question');
  const more = missing.length > 3 ? ` and ${missing.length - 3} more` : '';
  return `Answer the required questions to complete this form: ${names.join(', ')}${more}`;
}

export default function FormFill({ formId, shiftId, clientId, draft, title, onSubmitted, onCancel }) {
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed]   = useState(false);
  const [answers, setAnswers] = useState(() => ({ ...((draft && draft.responses) || {}) }));
  const [saving, setSaving]   = useState(null); // 'draft' | 'completed' while a save is running
  const request = useRef(0);

  // Only the latest request may land (and none after the form has closed).
  useEffect(() => () => { request.current += 1; }, []);

  const load = useCallback(() => {
    if (!formId) return;
    const mine = ++request.current;
    setLoading(true);
    setFailed(false);
    api.get(`/forms/${formId}`)
      .then(r => { if (mine === request.current) setForm(r.data); })
      .catch(() => { if (mine === request.current) setFailed(true); })
      .finally(() => { if (mine === request.current) setLoading(false); });
  }, [formId]);

  useEffect(() => { load(); }, [load]);

  function setField(q, v) { setAnswers(a => ({ ...a, [keyOf(q)]: v })); }

  async function save(status) {
    if (!form || saving) return;
    const isFinal = status === 'completed';
    if (isFinal) {
      const missing = missingRequired(questionsOf(form), answers);
      if (missing.length) {
        toast.error(missingMessage(missing));
        return;
      }
    }
    setSaving(status);
    try {
      // The server reads the answers from `responses`. They used to be sent as `answers`, which the
      // server ignored, so every form filled in here was saved with no answers at all. With no
      // `status` the server saves a draft, which is why every form filled in here stayed one.
      await api.post(`/forms/${formId}/submit`, {
        shiftId: shiftId || null,
        clientId: clientId || null,
        responses: answers,
        status,
        title: title || null,
      });
      toast.success(isFinal ? 'Form completed' : 'Draft saved');
      if (onSubmitted) onSubmitted(status);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the form. Try again.');
    } finally {
      setSaving(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading form">
        <div className="skeleton h-5 w-1/2" />
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="space-y-1.5">
            <div className="skeleton h-3 w-1/3" />
            <div className="skeleton h-9 w-full" />
          </div>
        ))}
      </div>
    );
  }
  if (failed || !form) {
    return <ErrorState message="We couldn't load this form. Check your connection, then try again." onRetry={load} />;
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-slate-100 pb-3">
        <h3 className="font-semibold text-slate-900">{form.name}</h3>
        {form.description && <p className="mt-1 text-[13px] leading-relaxed text-slate-500">{form.description}</p>}
        {draft && <p className="mt-1 text-[12.5px] text-slate-500">Carrying on from the saved draft.</p>}
      </div>

      <div className="space-y-4">
        {questionsOf(form).map((q, i) => (
          <FormField key={`${keyOf(q) || 'q'}-${i}`} question={q} value={answers[keyOf(q)]} onChange={v => setField(q, v)} />
        ))}
      </div>

      <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
        {onCancel && <button type="button" onClick={onCancel} className="btn-secondary" disabled={!!saving}>Cancel</button>}
        <button type="button" onClick={() => save('draft')} disabled={!!saving} className="btn-secondary">
          {saving === 'draft' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save draft
        </button>
        <button type="button" onClick={() => save('completed')} disabled={!!saving} className="btn-primary">
          {saving === 'completed' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          Complete and submit
        </button>
      </div>
    </div>
  );
}

const LEGEND = 'mb-1.5 text-[13px] font-medium text-slate-700';
const Required = ({ show }) => (show ? <span className="ml-0.5 text-rose-500" aria-hidden="true">*</span> : null);

// One field renderer per type
function FormField({ question: q, value, onChange }) {
  const groupName = useId();
  const required = !!q.required;
  const aria = { 'aria-required': required || undefined };
  const options = Array.isArray(q.options) ? q.options : [];

  switch (q.type) {
    case 'heading':
      // A section title, not a question.
      return <h4 className="border-b border-slate-100 pb-1.5 pt-2 text-[13.5px] font-semibold text-slate-900">{q.label}</h4>;
    case 'textarea':
      return (
        <Field label={q.label} required={required} hint={q.hint}>
          {id => <textarea id={id} className="input-field" rows={3} value={value || ''} onChange={e => onChange(e.target.value)} {...aria} />}
        </Field>
      );
    case 'number':
    case 'date':
    case 'time':
      return (
        <Field label={q.label} required={required} hint={q.hint}>
          {id => <input id={id} type={q.type} className="input-field" value={value || ''} onChange={e => onChange(e.target.value)} {...aria} />}
        </Field>
      );
    case 'select':
      return (
        <Field label={q.label} required={required} hint={q.hint}>
          {id => (
            <select id={id} className="input-field" value={value || ''} onChange={e => onChange(e.target.value)} {...aria}>
              <option value="">Select…</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
        </Field>
      );
    case 'radio':
      return (
        <fieldset className="min-w-0">
          <legend className={LEGEND}>{q.label}<Required show={required} /></legend>
          <div className="space-y-1.5">
            {options.map(o => (
              <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input type="radio" name={groupName} value={o} checked={value === o} onChange={e => onChange(e.target.value)}
                  className="h-4 w-4 shrink-0 accent-primary-600" />
                {o}
              </label>
            ))}
          </div>
          {q.hint && <p className="mt-1 text-[12px] leading-relaxed text-slate-500">{q.hint}</p>}
        </fieldset>
      );
    case 'checkbox': {
      const arr = Array.isArray(value) ? value : [];
      return (
        <fieldset className="min-w-0">
          <legend className={LEGEND}>{q.label}<Required show={required} /></legend>
          <div className="space-y-1.5">
            {options.map(o => (
              <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={arr.includes(o)}
                  onChange={e => onChange(e.target.checked ? [...arr, o] : arr.filter(x => x !== o))}
                  className="h-4 w-4 shrink-0 rounded accent-primary-600" />
                {o}
              </label>
            ))}
          </div>
          {q.hint && <p className="mt-1 text-[12px] leading-relaxed text-slate-500">{q.hint}</p>}
        </fieldset>
      );
    }
    case 'boolean':
    case 'yesno':
      // An explicit Yes / No (as on the Forms page): "No" is an answer, an untouched question is not.
      return (
        <fieldset className="min-w-0">
          <legend className={LEGEND}>{q.label}<Required show={required} /></legend>
          <div className="flex gap-4">
            {[['Yes', true], ['No', false]].map(([text, v]) => (
              <label key={text} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input type="radio" name={groupName} checked={value === v} onChange={() => onChange(v)}
                  className="h-4 w-4 shrink-0 accent-primary-600" />
                {text}
              </label>
            ))}
          </div>
          {q.hint && <p className="mt-1 text-[12px] leading-relaxed text-slate-500">{q.hint}</p>}
        </fieldset>
      );
    case 'signature':
      return <SignatureField label={q.label} required={required} value={value} onChange={onChange} />;
    default:
      return (
        <Field label={q.label} required={required} hint={q.hint}>
          {id => <input id={id} type="text" className="input-field" value={value || ''} onChange={e => onChange(e.target.value)} {...aria} />}
        </Field>
      );
  }
}

// Simple canvas-based signature capture
function SignatureField({ label, required, value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const labelId = useId();

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';

    if (value && typeof value === 'string') {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, []);

  // The canvas draws at 400×120 but is shown at the width of the form, so a pointer position is
  // scaled into canvas units; unscaled, the line landed away from the pen on any wider screen.
  function pos(e) {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    return { x: x * (c.width / (r.width || c.width)), y: y * (c.height / (r.height || c.height)) };
  }

  function start(e) {
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  }
  function move(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  }
  function clear() {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    onChange(null);
  }

  return (
    <div>
      <p id={labelId} className="mb-1 text-[13px] font-medium text-slate-700">{label}<Required show={required} /></p>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        <canvas ref={canvasRef} width="400" height="120" role="img" aria-labelledby={labelId}
          className="block w-full cursor-crosshair touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}/>
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-3 py-1">
          <span className="text-[12px] text-slate-500">Sign above</span>
          <button type="button" onClick={clear}
            className="rounded-md px-2.5 py-1.5 text-[12px] font-medium text-slate-600 transition-colors duration-150 hover:bg-slate-100 hover:text-rose-600">
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
